import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useApi, type MessageItem } from '@/shared/api';

const PAGE_SIZE = 100;

/**
 * Der ganze Verlauf einer Unterhaltung.
 *
 * Der Server blättert ab der ältesten Nachricht (der Cursor zeigt auf „danach“). Die Seite holte
 * früher nur die erste Seite und zeigte bei langen Gesprächen die ältesten Nachrichten statt der
 * neuesten. Darum lädt der Verlauf Seite um Seite nach, bis keine mehr kommt; gescheitert hält er
 * an, statt in einer Schleife weiterzufragen.
 */
export function useConversationMessages(id: string | null, enabled = true) {
  const api = useApi();
  const query = useInfiniteQuery({
    queryKey: ['inbox', 'messages', id],
    queryFn: ({ pageParam }) =>
      api.messaging.listMessages(id!, { cursor: pageParam ?? undefined, take: PAGE_SIZE }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: enabled && id !== null,
    refetchInterval: 30_000,
  });
  const { hasNextPage, isFetchingNextPage, isError, fetchNextPage } = query;

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isError) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, isError, fetchNextPage]);

  const items: MessageItem[] = query.data?.pages.flatMap((page) => page.items) ?? [];
  return {
    items,
    isLoading: query.isLoading,
    /** Ohne Verbindung pausiert: weder lädt der Verlauf, noch ist er leer. */
    paused: query.isPending && query.fetchStatus === 'paused',
    isError: query.isError,
    error: query.error,
    /** Noch nicht alle Seiten da: die neueste Nachricht kann fehlen. */
    incomplete: Boolean(hasNextPage),
    refetch: () => void query.refetch(),
  };
}
